import React, { useState, useEffect } from 'react';
import { CreditCard, Key, HelpCircle, Mail, Save, RotateCcw, AlertCircle, CheckCircle, Send, Globe, Clock, RefreshCw, Rocket, X, Check, BarChart3, Languages, Lock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import api from '../utils/api';
import Sidebar from '../components/Sidebar';
import SimpleTooltip from '../components/SimpleTooltip';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import { usePlanLimits } from '../hooks/usePlanLimits';

const GO_LIVE_CHECKLIST = [
  { id: 'pages', label: 'Created and published key pages (Home, About, Contact)' },
  { id: 'branding', label: 'Uploaded logo and set brand colors in Site Theme' },
  { id: 'navigation', label: 'Set up header and footer navigation menus' },
  { id: 'content', label: 'Added content to pages (text, images, etc.)' },
  { id: 'domain', label: 'Have a custom domain ready (e.g., myschool.com.au)' },
  { id: 'tested', label: 'Previewed and tested pages on different devices' },
];

const GoLiveModal = ({ isOpen, onClose }) => {
  const [checkedItems, setCheckedItems] = useState([]);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleCheckChange = (itemId, checked) => {
    if (checked) {
      setCheckedItems([...checkedItems, itemId]);
    } else {
      setCheckedItems(checkedItems.filter(id => id !== itemId));
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const completedLabels = GO_LIVE_CHECKLIST
        .filter(item => checkedItems.includes(item.id))
        .map(item => item.label);
      
      await api.post('/tenant/go-live-request', {
        checklist_completed: completedLabels,
        notes: notes || null
      });
      
      setSubmitted(true);
      toast.success('Go Live request submitted!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-xl">
        {/* Header */}
        <div className="p-6 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
              <Rocket className="w-5 h-5 text-emerald-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-900">Ready to Go Live?</h2>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {submitted ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-emerald-600" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-2">Request Submitted!</h3>
              <p className="text-slate-600 mb-6">
                We've received your Go Live request. Our team will review it and contact you within 1-2 business days.
              </p>
              <h4 className="font-semibold text-slate-900 mb-3">What happens next?</h4>
              <ul className="text-left text-sm text-slate-600 space-y-2 max-w-sm mx-auto">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500 mt-0.5">1.</span>
                  Our team will review your site
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500 mt-0.5">2.</span>
                  We'll help you set up your custom domain
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500 mt-0.5">3.</span>
                  DNS configuration and SSL setup
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500 mt-0.5">4.</span>
                  Final checks and your site goes live! 🎉
                </li>
              </ul>
              <Button onClick={onClose} className="mt-6">
                Close
              </Button>
            </div>
          ) : (
            <>
              <p className="text-slate-600 mb-6">
                Before we launch your site, let's make sure everything is ready. Check off the items you've completed:
              </p>

              {/* Checklist */}
              <div className="space-y-3 mb-6">
                {GO_LIVE_CHECKLIST.map((item) => (
                  <div key={item.id} className="flex items-start gap-3 p-3 rounded-lg hover:bg-slate-50">
                    <Checkbox
                      id={`settings-${item.id}`}
                      checked={checkedItems.includes(item.id)}
                      onCheckedChange={(checked) => handleCheckChange(item.id, checked)}
                    />
                    <Label htmlFor={`settings-${item.id}`} className="text-sm text-slate-700 cursor-pointer leading-relaxed">
                      {item.label}
                    </Label>
                  </div>
                ))}
              </div>

              {/* Notes */}
              <div className="mb-6">
                <Label htmlFor="settings-notes" className="text-sm font-medium text-slate-700 mb-2 block">
                  Additional notes or questions (optional)
                </Label>
                <Textarea
                  id="settings-notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="e.g., I'd like help setting up my domain, or I have questions about..."
                  rows={3}
                />
              </div>

              {/* Progress indicator */}
              <div className="mb-6 p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-slate-700">Checklist Progress</span>
                  <span className="text-sm text-slate-500">{checkedItems.length} of {GO_LIVE_CHECKLIST.length}</span>
                </div>
                <div className="w-full bg-slate-200 rounded-full h-2">
                  <div 
                    className="bg-emerald-500 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${(checkedItems.length / GO_LIVE_CHECKLIST.length) * 100}%` }}
                  />
                </div>
              </div>

              {/* Submit button */}
              <Button 
                onClick={handleSubmit} 
                disabled={submitting}
                className="w-full bg-emerald-600 hover:bg-emerald-700"
              >
                {submitting ? 'Submitting...' : '🚀 Submit Go Live Request'}
              </Button>

              <p className="text-xs text-slate-500 text-center mt-4">
                Don't worry if you haven't completed everything – we'll help you get there!
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const Settings = () => {
  const { user } = useAuth();
  const { planLevel: livePlanLevel } = usePlanLimits();
  const planLevel = (livePlanLevel || user?.plan || 'freemium').toLowerCase();
  const isFreemium = planLevel === 'freemium' || planLevel === 'free';

  const [processing, setProcessing] = useState(false);
  const [showGoLiveModal, setShowGoLiveModal] = useState(false);
  const [deploymentVersion, setDeploymentVersion] = useState(null);
  const [emailSettings, setEmailSettings] = useState({
    sender_email: '',
    sender_name: '',
    default_sender_email: '',
    is_custom: false,
    verification_status: null,
    is_verified: false,
    domain_auth_request: null,
    sendgrid_api_key_set: false,
    sendgrid_api_key_masked: null,
  });
  const [emailLoading, setEmailLoading] = useState(true);
  const [emailSaving, setEmailSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [showDomainAuth, setShowDomainAuth] = useState(false);
  const [domainAuthForm, setDomainAuthForm] = useState({ domain: '', notes: '' });
  const [sgKeyInput, setSgKeyInput] = useState('');
  const [sgKeySaving, setSgKeySaving] = useState(false);
  const [sgKeyTesting, setSgKeyTesting] = useState(false);
  
  // Google Analytics settings
  const [analyticsSettings, setAnalyticsSettings] = useState({
    ga_measurement_id: '',
    enable_builtin_analytics: true
  });
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const [analyticsSaving, setAnalyticsSaving] = useState(false);
  
  // Translation settings
  const [translationSettings, setTranslationSettings] = useState({
    translation_enabled: false,
    translation_tier: 'free',
    translation_positions: ['floating'],
    default_language: 'en',
    enabled_languages: null,
    translation_mode: 'selector'
  });
  const [translationLoading, setTranslationLoading] = useState(true);
  const [translationSaving, setTranslationSaving] = useState(false);
  
  // Cookie consent settings
  const [cookieSettings, setCookieSettings] = useState({
    enabled: false,
    message: 'We use cookies to enhance your browsing experience and analyse site traffic. By clicking "Accept", you consent to the use of cookies.',
    accept_text: 'Accept',
    decline_text: 'Decline',
    privacy_url: '',
    privacy_text: 'Privacy Policy',
    position: 'bottom',
    bg_color: '#1e293b',
    text_color: '#f8fafc',
    button_bg: '#4f46e5',
    button_text_color: '#ffffff',
    show_decline: true
  });
  const [cookieLoading, setCookieLoading] = useState(true);
  const [cookieSaving, setCookieSaving] = useState(false);
  
  // Billing state
  const [billingInfo, setBillingInfo] = useState(null);
  const [billingHistory, setBillingHistory] = useState([]);
  const [billingLoading, setBillingLoading] = useState(true);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [platformPricing, setPlatformPricing] = useState(null);

  useEffect(() => {
    // Check for payment session in URL
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    
    if (sessionId) {
      checkPaymentStatus(sessionId);
    }
    
    // Load email settings
    loadEmailSettings();
    
    // Load analytics settings
    loadAnalyticsSettings();
    
    // Load translation settings
    loadTranslationSettings();
    
    // Load cookie consent settings
    loadCookieSettings();
    
    // Load billing info
    loadBillingInfo();
    
    // Load live tier pricing (Stripe is source of truth)
    loadPlatformPricing();
    
    // Load deployment version
    loadDeploymentVersion();
  }, []);

  const loadPlatformPricing = async () => {
    try {
      const res = await api.get('/platform/pricing');
      setPlatformPricing(res.data);
    } catch (error) {
      console.log('Could not load platform pricing');
    }
  };
  const loadDeploymentVersion = async () => {
    try {
      const response = await api.get('/system/version');
      setDeploymentVersion(response.data);
    } catch (error) {
      // Version endpoint may require admin access
      console.log('Could not load version info');
    }
  };

  const loadAnalyticsSettings = async () => {
    setAnalyticsLoading(true);
    try {
      const response = await api.get('/tenant/analytics-settings');
      setAnalyticsSettings({
        ga_measurement_id: response.data.ga_measurement_id || '',
        enable_builtin_analytics: response.data.enable_builtin_analytics !== false
      });
    } catch (error) {
      console.log('Could not load analytics settings');
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const saveAnalyticsSettings = async () => {
    setAnalyticsSaving(true);
    try {
      await api.put('/tenant/analytics-settings', analyticsSettings);
      toast.success('Analytics settings saved successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save analytics settings');
    } finally {
      setAnalyticsSaving(false);
    }
  };

  const loadTranslationSettings = async () => {
    setTranslationLoading(true);
    try {
      const response = await api.get('/tenant/translation-settings');
      setTranslationSettings({
        translation_enabled: response.data.translation_enabled || false,
        translation_tier: response.data.translation_tier || 'free',
        translation_positions: response.data.translation_positions || ['floating'],
        default_language: response.data.default_language || 'en',
        enabled_languages: response.data.enabled_languages,
        translation_mode: response.data.translation_mode || 'selector'
      });
    } catch (error) {
      console.log('Could not load translation settings');
    } finally {
      setTranslationLoading(false);
    }
  };

  const saveTranslationSettings = async () => {
    setTranslationSaving(true);
    try {
      await api.put('/tenant/translation-settings', translationSettings);
      toast.success('Translation settings saved successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save translation settings');
    } finally {
      setTranslationSaving(false);
    }
  };

  const loadEmailSettings = async () => {
    try {
      const response = await api.get('/tenant/email-settings');
      setEmailSettings({
        sender_email: response.data.sender_email || '',
        sender_name: response.data.sender_name || '',
        default_sender_email: response.data.default_sender_email || '',
        is_custom: response.data.is_custom || false,
        verification_status: response.data.verification_status,
        is_verified: response.data.is_verified || false,
        domain_auth_request: response.data.domain_auth_request,
        sendgrid_api_key_set: response.data.sendgrid_api_key_set || false,
        sendgrid_api_key_masked: response.data.sendgrid_api_key_masked || null,
      });
    } catch (error) {
      console.error('Failed to load email settings:', error);
    } finally {
      setEmailLoading(false);
    }
  };

  // Cookie consent settings
  const loadCookieSettings = async () => {
    setCookieLoading(true);
    try {
      const tenantResp = await api.get('/tenant');
      const tenantId = tenantResp.data?.tenant_id;
      if (tenantId) {
        const response = await api.get(`/tenant/${tenantId}/cookie-consent`);
        setCookieSettings(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      console.log('Could not load cookie consent settings');
    } finally {
      setCookieLoading(false);
    }
  };

  const saveCookieSettings = async () => {
    setCookieSaving(true);
    try {
      const tenantResp = await api.get('/tenant');
      const tenantId = tenantResp.data?.tenant_id;
      if (tenantId) {
        await api.put(`/tenant/${tenantId}/cookie-consent`, cookieSettings);
        toast.success('Cookie consent settings saved successfully');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save cookie consent settings');
    } finally {
      setCookieSaving(false);
    }
  };

  const loadBillingInfo = async () => {
    setBillingLoading(true);
    try {
      const [infoRes, historyRes] = await Promise.all([
        api.get('/billing/info'),
        api.get('/billing/history')
      ]);
      setBillingInfo(infoRes.data);
      setBillingHistory(historyRes.data.transactions || []);
    } catch (error) {
      console.error('Failed to load billing info:', error);
    } finally {
      setBillingLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    setCancelling(true);
    try {
      const response = await api.post('/billing/cancel');
      toast.success(response.data.message);
      setShowCancelModal(false);
      loadBillingInfo(); // Refresh billing info
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel subscription');
    } finally {
      setCancelling(false);
    }
  };

  const requestVerification = async () => {
    if (!emailSettings.sender_email) {
      toast.error('Please enter an email address first');
      return;
    }
    
    setVerifying(true);
    try {
      const response = await api.post('/tenant/email-settings/verify-sender', {
        email: emailSettings.sender_email,
        name: emailSettings.sender_name || null
      });
      
      if (response.data.status === 'verified') {
        toast.success('Email is already verified!');
      } else {
        toast.success('Verification email sent! Check your inbox.');
      }
      loadEmailSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request verification');
    } finally {
      setVerifying(false);
    }
  };

  const checkVerificationStatus = async () => {
    setVerifying(true);
    try {
      const response = await api.get('/tenant/email-settings/check-verification');
      if (response.data.verified) {
        toast.success('Email is verified!');
      } else {
        toast.info('Still pending verification. Check your inbox.');
      }
      loadEmailSettings();
    } catch (error) {
      toast.error('Failed to check verification status');
    } finally {
      setVerifying(false);
    }
  };

  const resendVerification = async () => {
    setVerifying(true);
    try {
      const response = await api.post('/tenant/email-settings/resend-verification');
      if (response.data.already_verified) {
        toast.success('Email is already verified!');
      } else {
        toast.success('Verification email resent!');
      }
      loadEmailSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend verification');
    } finally {
      setVerifying(false);
    }
  };

  const requestDomainAuth = async () => {
    if (!domainAuthForm.domain) {
      toast.error('Please enter a domain');
      return;
    }
    
    setVerifying(true);
    try {
      const response = await api.post('/tenant/email-settings/request-domain-auth', domainAuthForm);
      toast.success(response.data.message);
      setShowDomainAuth(false);
      setDomainAuthForm({ domain: '', notes: '' });
      loadEmailSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    } finally {
      setVerifying(false);
    }
  };

  const saveEmailSettings = async () => {
    setEmailSaving(true);
    try {
      await api.put('/tenant/email-settings', {
        sender_email: emailSettings.sender_email || null,
        sender_name: emailSettings.sender_name || null
      });
      toast.success('Email settings saved successfully');
      loadEmailSettings(); // Reload to get updated state
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save email settings');
    } finally {
      setEmailSaving(false);
    }
  };

  const resetEmailSettings = async () => {
    setEmailSaving(true);
    try {
      await api.delete('/tenant/email-settings');
      setEmailSettings({
        ...emailSettings,
        sender_email: '',
        sender_name: '',
        is_custom: false,
        verification_status: null,
        is_verified: false
      });
      toast.success('Email settings reset to platform defaults');
    } catch (error) {
      toast.error('Failed to reset email settings');
    } finally {
      setEmailSaving(false);
    }
  };

  const handleSaveSgKey = async () => {
    setSgKeySaving(true);
    try {
      const res = await api.put('/tenant/email-settings/sendgrid-key', { sendgrid_api_key: sgKeyInput });
      setEmailSettings(s => ({
        ...s,
        sendgrid_api_key_set: res.data.sendgrid_api_key_set,
        sendgrid_api_key_masked: res.data.sendgrid_api_key_masked,
      }));
      setSgKeyInput('');
      toast.success(res.data.sendgrid_api_key_set ? 'Custom SendGrid key saved' : 'Custom SendGrid key removed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save SendGrid key');
    } finally {
      setSgKeySaving(false);
    }
  };

  const handleTestSgKey = async () => {
    setSgKeyTesting(true);
    try {
      const payload = sgKeyInput ? { sendgrid_api_key: sgKeyInput } : {};
      const res = await api.post('/tenant/email-settings/test-sendgrid-key', payload);
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Test email failed');
    } finally {
      setSgKeyTesting(false);
    }
  };

  const checkPaymentStatus = async (sessionId) => {
    try {
      const response = await api.get(`/billing/status/${sessionId}`);
      if (response.data.payment_status === 'paid') {
        toast.success('Payment successful! Your subscription is now active.');
      }
      // Clear URL params
      window.history.replaceState({}, '', '/settings');
    } catch (error) {
      console.error('Failed to check payment status:', error);
    }
  };

  const handleSubscribe = async (packageName) => {
    setProcessing(true);
    try {
      const response = await api.post('/billing/checkout', { package: packageName });
      window.location.href = response.data.url;
    } catch (error) {
      toast.error('Failed to create checkout session');
      setProcessing(false);
    }
  };

  const pTiers = platformPricing?.tiers || {};
  const salesEmail = platformPricing?.support_email || 'support@nexuscms.com';
  const packages = [
    {
      name: 'Freemium',
      slug: 'freemium',
      price: 0,
      priceLabel: 'Free',
      features: [
        '1 Landing Page',
        '1 Form',
        '250 MB Storage',
        'Affiliate Program Access',
        'Includes Dynatex branding',
      ],
    },
    {
      name: 'Starter',
      slug: 'starter',
      price: pTiers.basic?.price_monthly ?? 39,
      features: ['5 Pages', '1,000 Newsletter Emails/month', '1GB Storage', '2 Team Members'],
    },
    {
      name: 'Professional',
      slug: 'pro',
      price: pTiers.pro?.price_monthly ?? 99,
      features: ['Unlimited Pages', '10,000 Newsletter Emails/month', '10GB Storage', '10 Team Members', 'Priority Support'],
      recommended: true,
    },
    {
      name: 'Enterprise',
      slug: 'enterprise',
      price: null,
      contactSales: true,
      features: ['Unlimited Everything', 'Custom Domain', 'Dedicated Support', 'SLA', 'Advanced Analytics'],
    },
  ];

  return (
    <div className="flex" data-testid="settings-page">
      <Sidebar />
      
      <div className="flex-1 min-w-0 bg-slate-50 p-8">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <h1 className="text-4xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Settings
            </h1>
            <Button 
              onClick={() => setShowGoLiveModal(true)}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="settings-go-live-button"
            >
              <Rocket size={18} className="mr-2" />
              Ready to Go Live
            </Button>
          </div>

          {/* Billing Management Section */}
          <div className="mb-12" data-testid="billing-management-section">
            <div className="flex items-center gap-2 mb-6">
              <h2 className="text-2xl font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Billing Management
              </h2>
              <SimpleTooltip content="View your current plan, payment history, and manage your subscription">
                <HelpCircle size={18} className="text-slate-400 cursor-help" />
              </SimpleTooltip>
            </div>
            
            {billingLoading ? (
              <div className="bg-white rounded-xl p-8 text-center">
                <RefreshCw className="animate-spin mx-auto mb-2" size={24} />
                <p className="text-slate-500">Loading billing information...</p>
              </div>
            ) : billingInfo ? (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Current Plan Card */}
                <div className="bg-white rounded-xl border border-slate-200 p-6">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <CreditCard size={20} className="text-indigo-600" />
                    Current Plan
                  </h3>
                  
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-slate-600">Plan</span>
                      <span className="font-semibold text-lg">
                        {billingInfo.plan_name || 'Starter'}
                        {billingInfo.subscription_status === 'active' && (
                          <span className="ml-2 px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs rounded-full">Active</span>
                        )}
                        {billingInfo.subscription_status === 'trial' && (
                          <span className="ml-2 px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full">Trial</span>
                        )}
                        {billingInfo.subscription_status === 'cancelled' && (
                          <span className="ml-2 px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full">Cancelled</span>
                        )}
                      </span>
                    </div>
                    
                    {billingInfo.next_billing_date && (
                      <div className="flex justify-between items-center">
                        <span className="text-slate-600">Next Billing Date</span>
                        <span className="font-medium">{new Date(billingInfo.next_billing_date).toLocaleDateString()}</span>
                      </div>
                    )}
                    
                    {billingInfo.last_payment && (
                      <div className="flex justify-between items-center">
                        <span className="text-slate-600">Last Payment</span>
                        <span className="font-medium">
                          ${billingInfo.last_payment.amount?.toFixed(2)} on {new Date(billingInfo.last_payment.date).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                    
                    <div className="flex justify-between items-center">
                      <span className="text-slate-600">Member Since</span>
                      <span className="font-medium">
                        {billingInfo.member_since ? new Date(billingInfo.member_since).toLocaleDateString() : 'N/A'}
                      </span>
                    </div>
                    
                    {billingInfo.subscription_status === 'active' && (
                      <div className="pt-4 border-t border-slate-100">
                        <Button
                          variant="outline"
                          onClick={() => setShowCancelModal(true)}
                          className="text-red-600 border-red-200 hover:bg-red-50"
                          data-testid="cancel-subscription-btn"
                        >
                          Cancel Subscription
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Payment History Card */}
                <div className="bg-white rounded-xl border border-slate-200 p-6">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Clock size={20} className="text-indigo-600" />
                    Payment History
                  </h3>
                  
                  {billingHistory.length > 0 ? (
                    <div className="space-y-3 max-h-64 overflow-y-auto">
                      {billingHistory.map((tx, idx) => (
                        <div key={tx.transaction_id || idx} className="flex justify-between items-center py-2 border-b border-slate-100 last:border-0">
                          <div>
                            <p className="font-medium text-sm">
                              {tx.package ? tx.package.charAt(0).toUpperCase() + tx.package.slice(1) : 'Subscription'} Plan
                            </p>
                            <p className="text-xs text-slate-500">
                              {new Date(tx.created_at).toLocaleDateString()}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold">${tx.amount?.toFixed(2) || '0.00'}</p>
                            <p className={`text-xs ${
                              tx.payment_status === 'paid' ? 'text-emerald-600' : 
                              tx.payment_status === 'initiated' ? 'text-amber-600' : 'text-slate-500'
                            }`}>
                              {tx.payment_status === 'paid' ? '✓ Paid' : 
                               tx.payment_status === 'initiated' ? 'Pending' : tx.payment_status}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-slate-500">
                      <CreditCard size={32} className="mx-auto mb-2 opacity-50" />
                      <p>No payment history yet</p>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-xl p-8 text-center">
                <AlertCircle className="mx-auto mb-2 text-amber-500" size={32} />
                <p className="text-slate-600">Could not load billing information</p>
                <Button onClick={loadBillingInfo} variant="outline" className="mt-4">
                  <RefreshCw size={16} className="mr-2" />
                  Retry
                </Button>
              </div>
            )}
          </div>

          {/* Cancel Subscription Modal */}
          {showCancelModal && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-xl max-w-md w-full p-6 shadow-xl">
                <h3 className="text-xl font-bold mb-4 text-red-600">Cancel Subscription</h3>
                <p className="text-slate-600 mb-4">
                  Are you sure you want to cancel your subscription? You will retain access until the end of your current billing period.
                </p>
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setShowCancelModal(false)}
                    className="flex-1"
                  >
                    Keep Subscription
                  </Button>
                  <Button
                    onClick={handleCancelSubscription}
                    disabled={cancelling}
                    className="flex-1 bg-red-600 hover:bg-red-700"
                  >
                    {cancelling ? 'Cancelling...' : 'Yes, Cancel'}
                  </Button>
                </div>
              </div>
            </div>
          )}

          <div className="mb-12">
            <div className="flex items-center gap-2 mb-6">
              <h2 className="text-2xl font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Subscription Plans
              </h2>
              <SimpleTooltip content="Choose a plan that fits your needs. You can upgrade or downgrade anytime.">
                <HelpCircle size={18} className="text-slate-400 cursor-help" />
              </SimpleTooltip>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {packages.map((pkg) => {
                const isCurrent = planLevel === pkg.slug || (planLevel === 'free' && pkg.slug === 'freemium');
                return (
                <div
                  key={pkg.slug}
                  data-testid={`package-${pkg.slug}`}
                  className={`bg-white rounded-xl border-2 p-6 hover:shadow-lg transition-all ${
                    isCurrent ? 'border-emerald-500' : pkg.recommended ? 'border-indigo-600' : 'border-slate-200'
                  }`}
                >
                  {isCurrent ? (
                    <span className="inline-block px-3 py-1 bg-emerald-500 text-white text-xs font-semibold rounded-full mb-4">
                      Current Plan
                    </span>
                  ) : pkg.recommended ? (
                    <span className="inline-block px-3 py-1 bg-indigo-600 text-white text-xs font-semibold rounded-full mb-4">
                      Recommended
                    </span>
                  ) : null}
                  <h3 className="text-2xl font-bold mb-2">{pkg.name}</h3>
                  <p className="text-4xl font-bold mb-6">
                    {pkg.contactSales ? (
                      <>Contact us</>
                    ) : pkg.price === 0 ? (
                      <>{pkg.priceLabel || 'Free'}<span className="text-lg text-slate-500 font-normal"> forever</span></>
                    ) : (
                      <>${pkg.price}<span className="text-lg text-slate-500 font-normal">/mo</span></>
                    )}
                  </p>
                  <ul className="space-y-3 mb-6">
                    {pkg.features.map((feature) => (
                      <li key={feature} className="flex items-center gap-2 text-sm">
                        <span className="text-emerald-600">✓</span>
                        {feature}
                      </li>
                    ))}
                  </ul>
                  {isCurrent ? (
                    <Button
                      disabled
                      className="w-full bg-slate-200 text-slate-600 cursor-not-allowed"
                      data-testid={`subscribe-${pkg.slug}-current`}
                    >
                      <Check size={18} className="mr-2" />
                      Current Plan
                    </Button>
                  ) : pkg.slug === 'freemium' ? (
                    <Button
                      disabled
                      className="w-full bg-slate-100 text-slate-500 cursor-not-allowed"
                      title="Freemium is the default plan — cancel a paid subscription to return to Freemium"
                      data-testid={`subscribe-${pkg.slug}`}
                    >
                      Default Plan
                    </Button>
                  ) : pkg.contactSales ? (
                    <Button
                      onClick={() => { window.location.href = `mailto:${salesEmail}?subject=${encodeURIComponent('Enterprise plan enquiry')}`; }}
                      className="w-full bg-slate-900 hover:bg-slate-800"
                      data-testid={`subscribe-${pkg.slug}`}
                    >
                      Contact Sales
                    </Button>
                  ) : (
                    <Button
                      onClick={() => handleSubscribe(pkg.slug)}
                      disabled={processing}
                      className={`w-full ${
                        pkg.recommended
                          ? 'bg-indigo-600 hover:bg-indigo-700'
                          : 'bg-slate-900 hover:bg-slate-800'
                      }`}
                      data-testid={`subscribe-${pkg.slug}`}
                    >
                      <CreditCard size={18} className="mr-2" />
                      Subscribe
                    </Button>
                  )}
                </div>
                );
              })}
            </div>
          </div>

          <div className="mb-12">
            <div className="flex items-center gap-2 mb-6">
              <h2 className="text-2xl font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Email Settings
              </h2>
              <SimpleTooltip content="Configure your custom sender email for form notifications and newsletters">
                <HelpCircle size={18} className="text-slate-400 cursor-help" />
              </SimpleTooltip>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              {emailLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                </div>
              ) : (
                <>
                  {/* Single Sender Section */}
                  <div className="flex items-start gap-4 mb-6">
                    <div className="w-12 h-12 rounded-lg bg-indigo-100 flex items-center justify-center">
                      <Mail size={24} className="text-indigo-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold mb-2">Single Sender Verification</h3>
                      <p className="text-slate-600 text-sm">
                        Set a custom "From" address for emails. Enter your email and click verify - we'll send a confirmation link to your inbox.
                      </p>
                    </div>
                  </div>
                  
                  <div className="space-y-4 mb-6">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Sender Email Address
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="email"
                          value={emailSettings.sender_email}
                          onChange={(e) => setEmailSettings({...emailSettings, sender_email: e.target.value, is_verified: false, verification_status: null})}
                          placeholder={emailSettings.default_sender_email || 'noreply@yourcompany.com'}
                          className="flex-1 px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                          data-testid="sender-email-input"
                        />
                        <Button 
                          onClick={requestVerification}
                          disabled={verifying || !emailSettings.sender_email}
                          className="bg-indigo-600 hover:bg-indigo-700"
                          data-testid="verify-email-btn"
                        >
                          <Send size={16} className="mr-2" />
                          {verifying ? 'Sending...' : 'Verify'}
                        </Button>
                      </div>
                      <p className="text-xs text-slate-500 mt-1">
                        Platform default: {emailSettings.default_sender_email}
                      </p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Sender Name
                      </label>
                      <input
                        type="text"
                        value={emailSettings.sender_name}
                        onChange={(e) => setEmailSettings({...emailSettings, sender_name: e.target.value})}
                        placeholder="Your Company Name"
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        data-testid="sender-name-input"
                      />
                    </div>
                  </div>
                  
                  {/* Verification Status */}
                  {emailSettings.is_custom && (
                    <div className={`flex items-center justify-between p-4 rounded-lg mb-6 ${
                      emailSettings.is_verified ? 'bg-emerald-50 border border-emerald-200' : 'bg-amber-50 border border-amber-200'
                    }`}>
                      <div className="flex items-center gap-2">
                        {emailSettings.is_verified ? (
                          <>
                            <CheckCircle size={20} className="text-emerald-600" />
                            <div>
                              <span className="text-sm font-medium text-emerald-700">Verified</span>
                              <p className="text-xs text-emerald-600">{emailSettings.sender_email} is ready to use</p>
                            </div>
                          </>
                        ) : (
                          <>
                            <Clock size={20} className="text-amber-600" />
                            <div>
                              <span className="text-sm font-medium text-amber-700">Pending Verification</span>
                              <p className="text-xs text-amber-600">Check your inbox for the verification email</p>
                            </div>
                          </>
                        )}
                      </div>
                      {!emailSettings.is_verified && (
                        <div className="flex gap-2">
                          <Button 
                            onClick={checkVerificationStatus}
                            disabled={verifying}
                            variant="outline"
                            size="sm"
                            className="text-amber-700 border-amber-300 hover:bg-amber-100"
                          >
                            <RefreshCw size={14} className={`mr-1 ${verifying ? 'animate-spin' : ''}`} />
                            Check Status
                          </Button>
                          <Button 
                            onClick={resendVerification}
                            disabled={verifying}
                            variant="outline"
                            size="sm"
                            className="text-amber-700 border-amber-300 hover:bg-amber-100"
                          >
                            <Send size={14} className="mr-1" />
                            Resend
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {!emailSettings.is_custom && (
                    <div className="flex items-center gap-2 p-3 rounded-lg mb-6 bg-slate-50">
                      <AlertCircle size={18} className="text-slate-500" />
                      <span className="text-sm text-slate-600">
                        Using platform default sender
                      </span>
                    </div>
                  )}
                  
                  <div className="flex gap-3 mb-8">
                    <Button 
                      onClick={saveEmailSettings} 
                      disabled={emailSaving}
                      className="bg-indigo-600 hover:bg-indigo-700"
                      data-testid="save-email-settings"
                    >
                      <Save size={18} className="mr-2" />
                      {emailSaving ? 'Saving...' : 'Save Settings'}
                    </Button>
                    {emailSettings.is_custom && (
                      <Button 
                        onClick={resetEmailSettings} 
                        disabled={emailSaving}
                        variant="outline"
                        data-testid="reset-email-settings"
                      >
                        <RotateCcw size={18} className="mr-2" />
                        Reset to Default
                      </Button>
                    )}
                  </div>

                  {/* Domain Authentication Section */}
                  <div className="border-t border-slate-200 pt-6">
                    <div className="flex items-start gap-4 mb-4">
                      <div className="w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center">
                        <Globe size={24} className="text-purple-600" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold">Domain Authentication</h3>
                          <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-medium rounded">Premium</span>
                        </div>
                        <p className="text-slate-600 text-sm mt-1">
                          Authenticate your entire domain for better deliverability. Any email @yourdomain.com will work automatically.
                        </p>
                      </div>
                    </div>
                    
                    {emailSettings.domain_auth_request ? (
                      <div className={`p-4 rounded-lg ${
                        emailSettings.domain_auth_request.status === 'verified' 
                          ? 'bg-emerald-50 border border-emerald-200' 
                          : 'bg-blue-50 border border-blue-200'
                      }`}>
                        <div className="flex items-center gap-2 mb-2">
                          {emailSettings.domain_auth_request.status === 'verified' ? (
                            <CheckCircle size={18} className="text-emerald-600" />
                          ) : (
                            <Clock size={18} className="text-blue-600" />
                          )}
                          <span className={`text-sm font-medium ${
                            emailSettings.domain_auth_request.status === 'verified' ? 'text-emerald-700' : 'text-blue-700'
                          }`}>
                            {emailSettings.domain_auth_request.status === 'verified' 
                              ? 'Domain Authenticated' 
                              : 'Request Pending'}
                          </span>
                        </div>
                        <p className="text-sm text-slate-600">
                          Domain: <strong>{emailSettings.domain_auth_request.domain}</strong>
                        </p>
                        {emailSettings.domain_auth_request.status === 'pending' && (
                          <p className="text-xs text-slate-500 mt-1">
                            Our team will contact you within 1-2 business days with DNS records.
                          </p>
                        )}
                      </div>
                    ) : showDomainAuth ? (
                      <div className="bg-slate-50 rounded-lg p-4">
                        <div className="space-y-3">
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">
                              Your Domain
                            </label>
                            <input
                              type="text"
                              value={domainAuthForm.domain}
                              onChange={(e) => setDomainAuthForm({...domainAuthForm, domain: e.target.value})}
                              placeholder="yourcompany.com"
                              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">
                              Notes (optional)
                            </label>
                            <textarea
                              value={domainAuthForm.notes}
                              onChange={(e) => setDomainAuthForm({...domainAuthForm, notes: e.target.value})}
                              placeholder="Any additional information..."
                              rows={2}
                              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                            />
                          </div>
                          <div className="flex gap-2">
                            <Button 
                              onClick={requestDomainAuth}
                              disabled={verifying || !domainAuthForm.domain}
                              className="bg-purple-600 hover:bg-purple-700"
                            >
                              {verifying ? 'Submitting...' : 'Submit Request'}
                            </Button>
                            <Button 
                              onClick={() => setShowDomainAuth(false)}
                              variant="outline"
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <Button 
                        onClick={() => setShowDomainAuth(true)}
                        variant="outline"
                        className="border-purple-300 text-purple-700 hover:bg-purple-50"
                      >
                        <Globe size={16} className="mr-2" />
                        Request Domain Authentication
                      </Button>
                    )}
                  </div>

                  {/* Custom SendGrid API Key */}
                  <div className="border-t pt-6 mt-4">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-slate-800">Custom SendGrid API Key</h3>
                      {emailSettings.sendgrid_api_key_set && (
                        <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700 rounded-full border border-green-200">Active</span>
                      )}
                    </div>
                    <p className="text-sm text-slate-500 mb-4">
                      Use your own SendGrid account for newsletters, keeping your email sending reputation fully isolated from the platform. Leave blank to use the platform's shared account.
                    </p>
                    {emailSettings.sendgrid_api_key_set && (
                      <div className="flex items-center gap-2 mb-3 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
                        <span>Current key: <code className="font-mono">{emailSettings.sendgrid_api_key_masked}</code></span>
                        <button
                          className="ml-auto text-xs underline text-red-500 hover:text-red-700"
                          onClick={async () => { setSgKeyInput(''); await handleSaveSgKey(); }}
                        >Remove</button>
                      </div>
                    )}
                    <div className="flex gap-2 items-end">
                      <div className="flex-1 space-y-1">
                        <label className="text-xs text-slate-500">SendGrid API Key</label>
                        <input
                          type="password"
                          className="flex h-9 w-full rounded-md border border-slate-200 bg-white px-3 py-1 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 font-mono"
                          placeholder={emailSettings.sendgrid_api_key_set ? 'Enter new key to replace…' : 'SG.xxxx…'}
                          value={sgKeyInput}
                          onChange={(e) => setSgKeyInput(e.target.value)}
                          data-testid="sendgrid-api-key-input"
                        />
                      </div>
                      <button
                        className="h-9 px-4 rounded-md border border-slate-200 text-sm font-medium hover:bg-slate-50 disabled:opacity-50"
                        onClick={handleTestSgKey}
                        disabled={sgKeyTesting || (!sgKeyInput && !emailSettings.sendgrid_api_key_set)}
                        data-testid="test-sendgrid-key-btn"
                      >
                        {sgKeyTesting ? 'Sending…' : 'Test'}
                      </button>
                      <button
                        className="h-9 px-4 rounded-md bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-50"
                        onClick={handleSaveSgKey}
                        disabled={sgKeySaving || !sgKeyInput}
                        data-testid="save-sendgrid-key-btn"
                      >
                        {sgKeySaving ? 'Saving…' : 'Save Key'}
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Plan-restricted sections (Freemium gets a locked placeholder) */}
          {isFreemium ? (
            <div className="mb-12" data-testid="freemium-locked-settings">
              <div className="flex items-center gap-2 mb-6">
                <h2 className="text-2xl font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  More Settings
                </h2>
              </div>
              <div className="bg-white rounded-xl border border-dashed border-slate-300 p-8 text-center">
                <div className="w-12 h-12 mx-auto rounded-full bg-slate-100 flex items-center justify-center mb-3">
                  <Lock className="text-slate-500" size={22} />
                </div>
                <h3 className="text-lg font-semibold text-slate-800 mb-1">
                  Analytics, Translations, API Access &amp; Cookie Banner are paid features
                </h3>
                <p className="text-sm text-slate-500 mb-5">
                  Upgrade to a paid plan to unlock Google Analytics, multi-language translations,
                  the REST API and a customisable cookie consent banner.
                </p>
                <Button
                  asChild
                  className="bg-indigo-600 hover:bg-indigo-700"
                  data-testid="freemium-upgrade-from-locked-settings"
                >
                  <a href="#billing-management-section">View Upgrade Options</a>
                </Button>
              </div>
            </div>
          ) : (
          <>
          {/* Analytics Settings */}
          <div className="mb-12">
            <div className="flex items-center gap-2 mb-6">
              <h2 className="text-2xl font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Analytics Settings
              </h2>
              <SimpleTooltip content="Configure how you track visitor analytics on your public site">
                <HelpCircle size={18} className="text-slate-400 cursor-help" />
              </SimpleTooltip>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              {analyticsLoading ? (
                <p className="text-slate-500">Loading analytics settings...</p>
              ) : (
                <div className="space-y-6">
                  {/* Google Analytics */}
                  <div>
                    <div className="flex items-start gap-4 mb-4">
                      <div className="w-12 h-12 rounded-lg bg-orange-100 flex items-center justify-center">
                        <BarChart3 size={24} className="text-orange-600" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold mb-1">Google Analytics 4</h3>
                        <p className="text-sm text-slate-600">
                          Add your GA4 Measurement ID to enable Google Analytics tracking on your public site.
                        </p>
                      </div>
                    </div>
                    <div className="ml-16">
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        GA4 Measurement ID
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={analyticsSettings.ga_measurement_id}
                          onChange={(e) => setAnalyticsSettings({...analyticsSettings, ga_measurement_id: e.target.value})}
                          placeholder="G-XXXXXXXXXX"
                          className="flex-1 max-w-xs px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 font-mono"
                        />
                      </div>
                      <p className="text-xs text-slate-500 mt-1">
                        Find this in Google Analytics → Admin → Data Streams → Your Stream → Measurement ID
                      </p>
                    </div>
                  </div>
                  
                  <hr className="border-slate-200" />
                  
                  {/* Built-in Analytics */}
                  <div>
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-lg bg-indigo-100 flex items-center justify-center">
                        <BarChart3 size={24} className="text-indigo-600" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold mb-1">Built-in Analytics</h3>
                        <p className="text-sm text-slate-600 mb-3">
                          Our privacy-friendly analytics - no cookies, GDPR compliant.
                        </p>
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={analyticsSettings.enable_builtin_analytics}
                            onChange={(e) => setAnalyticsSettings({...analyticsSettings, enable_builtin_analytics: e.target.checked})}
                            className="w-5 h-5 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                          />
                          <span className="text-sm text-slate-700">Enable built-in analytics tracking</span>
                        </label>
                      </div>
                    </div>
                  </div>
                  
                  {/* Save button */}
                  <div className="flex justify-end pt-4 border-t border-slate-200">
                    <Button 
                      onClick={saveAnalyticsSettings}
                      disabled={analyticsSaving}
                      className="bg-indigo-600 hover:bg-indigo-700"
                    >
                      {analyticsSaving ? 'Saving...' : 'Save Analytics Settings'}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Translation Settings */}
          <div className="mb-12" data-testid="translation-settings-section">
            <div className="flex items-center gap-2 mb-6">
              <h2 className="text-2xl font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Translation Settings
              </h2>
              <SimpleTooltip content="Configure how visitors can translate your public pages into different languages">
                <HelpCircle size={18} className="text-slate-400 cursor-help" />
              </SimpleTooltip>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              {translationLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Enable/Disable Translation Toggle */}
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
                        <Languages size={24} className="text-blue-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold">Enable Translation</h3>
                        <p className="text-sm text-slate-600 mt-1">
                          Allow visitors to translate your pages into their preferred language
                        </p>
                      </div>
                    </div>
                    <input
                      type="checkbox"
                      checked={translationSettings.translation_enabled}
                      onChange={(e) => setTranslationSettings({...translationSettings, translation_enabled: e.target.checked})}
                      className="w-5 h-5 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                      data-testid="translation-enabled-toggle"
                    />
                  </div>

                  {translationSettings.translation_enabled && (
                    <>
                      {/* Translation Mode */}
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-slate-700">
                          Auto-Translation Mode
                        </label>
                        <select
                          value={translationSettings.translation_mode || 'selector'}
                          onChange={(e) => setTranslationSettings({...translationSettings, translation_mode: e.target.value})}
                          className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                          data-testid="translation-mode-select"
                        >
                          <option value="selector">Manual Selection Only</option>
                          <option value="auto">Auto-detect (Browser Language)</option>
                        </select>
                        <p className="text-xs text-slate-500">
                          {translationSettings.translation_mode === 'auto' 
                            ? 'Pages will automatically translate based on visitor\'s browser language'
                            : 'Visitors must manually select their language from the language selector'}
                        </p>
                      </div>

                      {/* Translation Tier */}
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-slate-700">
                          Translation Tier
                        </label>
                        <select
                          value={translationSettings.translation_tier}
                          onChange={(e) => setTranslationSettings({...translationSettings, translation_tier: e.target.value})}
                          className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                          data-testid="translation-tier-select"
                        >
                          <option value="free">Free (Google Widget)</option>
                          <option value="premium">Premium (API - Higher Quality)</option>
                        </select>
                        <p className="text-xs text-slate-500">
                          {translationSettings.translation_tier === 'free' 
                            ? 'Uses Google Translate browser widget - no API costs'
                            : 'Uses Google Cloud Translation API - cached for cost efficiency'}
                        </p>
                      </div>

                      {/* Selector Positions */}
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-slate-700">
                          Language Selector Positions
                        </label>
                        <div className="space-y-2">
                          {[
                            { id: 'header', label: 'Header (Right Side)', desc: 'In the navigation menu' },
                            { id: 'footer', label: 'Footer', desc: 'At the bottom of the page' },
                            { id: 'floating', label: 'Floating', desc: 'Fixed position (top-left corner)' }
                          ].map((pos) => (
                            <div key={pos.id} className="flex items-center gap-3 p-2 rounded border border-slate-200">
                              <input
                                type="checkbox"
                                id={`settings-pos-${pos.id}`}
                                checked={translationSettings.translation_positions?.includes(pos.id)}
                                onChange={(e) => {
                                  const positions = translationSettings.translation_positions || [];
                                  if (e.target.checked) {
                                    setTranslationSettings({
                                      ...translationSettings,
                                      translation_positions: [...positions, pos.id]
                                    });
                                  } else {
                                    setTranslationSettings({
                                      ...translationSettings,
                                      translation_positions: positions.filter(p => p !== pos.id)
                                    });
                                  }
                                }}
                                className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                              />
                              <div>
                                <label htmlFor={`settings-pos-${pos.id}`} className="text-sm font-medium cursor-pointer">
                                  {pos.label}
                                </label>
                                <p className="text-xs text-slate-500">{pos.desc}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Default Language */}
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-slate-700">
                          Default Language
                        </label>
                        <select
                          value={translationSettings.default_language}
                          onChange={(e) => setTranslationSettings({...translationSettings, default_language: e.target.value})}
                          className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                          data-testid="default-language-select"
                        >
                          <option value="en">English</option>
                          <option value="es">Spanish</option>
                          <option value="fr">French</option>
                          <option value="de">German</option>
                          <option value="it">Italian</option>
                          <option value="pt">Portuguese</option>
                          <option value="zh">Chinese</option>
                          <option value="ja">Japanese</option>
                          <option value="ko">Korean</option>
                          <option value="ar">Arabic</option>
                          <option value="hi">Hindi</option>
                          <option value="pl">Polish</option>
                        </select>
                        <p className="text-xs text-slate-500">The original language of your website content</p>
                      </div>
                    </>
                  )}
                  
                  {/* Save button */}
                  <div className="flex justify-end pt-4 border-t border-slate-200">
                    <Button 
                      onClick={saveTranslationSettings}
                      disabled={translationSaving}
                      className="bg-indigo-600 hover:bg-indigo-700"
                      data-testid="save-translation-settings-btn"
                    >
                      {translationSaving ? 'Saving...' : 'Save Translation Settings'}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div>
            <div className="flex items-center gap-2 mb-6">
              <h2 className="text-2xl font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                API Access
              </h2>
              <SimpleTooltip content="Use our REST API to integrate NexusCMS with external systems">
                <HelpCircle size={18} className="text-slate-400 cursor-help" />
              </SimpleTooltip>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center">
                  <Key size={24} className="text-slate-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold mb-2">API Documentation</h3>
                  <p className="text-slate-600 mb-4">
                    Access our REST API to integrate NexusCMS with your applications.
                  </p>
                  <Button variant="outline" asChild>
                    <a href="/api-docs">View API Docs</a>
                  </Button>
                </div>
              </div>
            </div>
          </div>
          
          {/* Cookie Consent Banner Settings */}
          <div className="mb-12" data-testid="cookie-consent-section">
            <h2 className="text-lg font-semibold text-slate-800 mb-4">Cookie Consent Banner</h2>
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              {cookieLoading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Enable Toggle */}
                  <div className="flex items-center justify-between">
                    <div>
                      <label className="text-sm font-medium text-slate-700">Enable Cookie Banner</label>
                      <p className="text-xs text-slate-500 mt-0.5">Show a cookie consent banner to visitors</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={cookieSettings.enabled}
                        onChange={(e) => setCookieSettings(prev => ({ ...prev, enabled: e.target.checked }))}
                        className="sr-only peer"
                        data-testid="cookie-enabled-toggle"
                      />
                      <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-indigo-500 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                    </label>
                  </div>

                  {cookieSettings.enabled && (
                    <>
                      {/* Message */}
                      <div>
                        <label className="text-sm font-medium text-slate-700 mb-1 block">Banner Message</label>
                        <textarea
                          value={cookieSettings.message}
                          onChange={(e) => setCookieSettings(prev => ({ ...prev, message: e.target.value }))}
                          rows={3}
                          className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                          data-testid="cookie-message-input"
                        />
                      </div>

                      {/* Button Text */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm font-medium text-slate-700 mb-1 block">Accept Button Text</label>
                          <input
                            type="text"
                            value={cookieSettings.accept_text}
                            onChange={(e) => setCookieSettings(prev => ({ ...prev, accept_text: e.target.value }))}
                            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500"
                            data-testid="cookie-accept-text-input"
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium text-slate-700 mb-1 block">Decline Button Text</label>
                          <input
                            type="text"
                            value={cookieSettings.decline_text}
                            onChange={(e) => setCookieSettings(prev => ({ ...prev, decline_text: e.target.value }))}
                            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500"
                            data-testid="cookie-decline-text-input"
                          />
                        </div>
                      </div>

                      {/* Show Decline */}
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={cookieSettings.show_decline}
                          onChange={(e) => setCookieSettings(prev => ({ ...prev, show_decline: e.target.checked }))}
                          className="rounded border-slate-300"
                          data-testid="cookie-show-decline"
                        />
                        <label className="text-sm text-slate-700">Show decline button</label>
                      </div>

                      {/* Privacy Policy URL */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm font-medium text-slate-700 mb-1 block">Privacy Policy URL</label>
                          <input
                            type="url"
                            value={cookieSettings.privacy_url}
                            onChange={(e) => setCookieSettings(prev => ({ ...prev, privacy_url: e.target.value }))}
                            placeholder="https://example.com/privacy"
                            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500"
                            data-testid="cookie-privacy-url-input"
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium text-slate-700 mb-1 block">Privacy Link Text</label>
                          <input
                            type="text"
                            value={cookieSettings.privacy_text}
                            onChange={(e) => setCookieSettings(prev => ({ ...prev, privacy_text: e.target.value }))}
                            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500"
                            data-testid="cookie-privacy-text-input"
                          />
                        </div>
                      </div>

                      {/* Position */}
                      <div>
                        <label className="text-sm font-medium text-slate-700 mb-1 block">Position</label>
                        <div className="flex gap-2">
                          {['bottom', 'top'].map(pos => (
                            <button
                              key={pos}
                              onClick={() => setCookieSettings(prev => ({ ...prev, position: pos }))}
                              className={`px-4 py-2 text-sm rounded-lg border transition-colors ${
                                cookieSettings.position === pos
                                  ? 'bg-indigo-100 border-indigo-300 text-indigo-700 font-medium'
                                  : 'bg-white border-slate-300 text-slate-600 hover:bg-slate-50'
                              }`}
                              data-testid={`cookie-position-${pos}`}
                            >
                              {pos.charAt(0).toUpperCase() + pos.slice(1)}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Colours */}
                      <div>
                        <label className="text-sm font-medium text-slate-700 mb-2 block">Colours</label>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                          <div>
                            <label className="text-xs text-slate-500 mb-1 block">Background</label>
                            <div className="flex items-center gap-2">
                              <input
                                type="color"
                                value={cookieSettings.bg_color}
                                onChange={(e) => setCookieSettings(prev => ({ ...prev, bg_color: e.target.value }))}
                                className="w-8 h-8 rounded border border-slate-300 cursor-pointer"
                              />
                              <input
                                type="text"
                                value={cookieSettings.bg_color}
                                onChange={(e) => setCookieSettings(prev => ({ ...prev, bg_color: e.target.value }))}
                                className="flex-1 border border-slate-300 rounded px-2 py-1 text-xs font-mono"
                              />
                            </div>
                          </div>
                          <div>
                            <label className="text-xs text-slate-500 mb-1 block">Text</label>
                            <div className="flex items-center gap-2">
                              <input
                                type="color"
                                value={cookieSettings.text_color}
                                onChange={(e) => setCookieSettings(prev => ({ ...prev, text_color: e.target.value }))}
                                className="w-8 h-8 rounded border border-slate-300 cursor-pointer"
                              />
                              <input
                                type="text"
                                value={cookieSettings.text_color}
                                onChange={(e) => setCookieSettings(prev => ({ ...prev, text_color: e.target.value }))}
                                className="flex-1 border border-slate-300 rounded px-2 py-1 text-xs font-mono"
                              />
                            </div>
                          </div>
                          <div>
                            <label className="text-xs text-slate-500 mb-1 block">Button BG</label>
                            <div className="flex items-center gap-2">
                              <input
                                type="color"
                                value={cookieSettings.button_bg}
                                onChange={(e) => setCookieSettings(prev => ({ ...prev, button_bg: e.target.value }))}
                                className="w-8 h-8 rounded border border-slate-300 cursor-pointer"
                              />
                              <input
                                type="text"
                                value={cookieSettings.button_bg}
                                onChange={(e) => setCookieSettings(prev => ({ ...prev, button_bg: e.target.value }))}
                                className="flex-1 border border-slate-300 rounded px-2 py-1 text-xs font-mono"
                              />
                            </div>
                          </div>
                          <div>
                            <label className="text-xs text-slate-500 mb-1 block">Button Text</label>
                            <div className="flex items-center gap-2">
                              <input
                                type="color"
                                value={cookieSettings.button_text_color}
                                onChange={(e) => setCookieSettings(prev => ({ ...prev, button_text_color: e.target.value }))}
                                className="w-8 h-8 rounded border border-slate-300 cursor-pointer"
                              />
                              <input
                                type="text"
                                value={cookieSettings.button_text_color}
                                onChange={(e) => setCookieSettings(prev => ({ ...prev, button_text_color: e.target.value }))}
                                className="flex-1 border border-slate-300 rounded px-2 py-1 text-xs font-mono"
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Preview */}
                      <div>
                        <label className="text-sm font-medium text-slate-700 mb-2 block">Preview</label>
                        <div
                          className="rounded-lg p-4"
                          style={{ backgroundColor: cookieSettings.bg_color, color: cookieSettings.text_color }}
                        >
                          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                            <p className="text-sm flex-1" style={{ opacity: 0.9 }}>
                              {cookieSettings.message}
                              {cookieSettings.privacy_url && (
                                <span className="underline ml-1 font-medium">{cookieSettings.privacy_text}</span>
                              )}
                            </p>
                            <div className="flex gap-2 flex-shrink-0">
                              {cookieSettings.show_decline && (
                                <span
                                  className="px-3 py-1.5 text-sm rounded-lg border"
                                  style={{ color: cookieSettings.text_color, borderColor: `${cookieSettings.text_color}40` }}
                                >
                                  {cookieSettings.decline_text}
                                </span>
                              )}
                              <span
                                className="px-4 py-1.5 text-sm font-semibold rounded-lg"
                                style={{ backgroundColor: cookieSettings.button_bg, color: cookieSettings.button_text_color }}
                              >
                                {cookieSettings.accept_text}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  {/* Save Button */}
                  <div className="flex justify-end pt-2">
                    <Button
                      onClick={saveCookieSettings}
                      disabled={cookieSaving}
                      data-testid="save-cookie-settings-btn"
                    >
                      {cookieSaving ? 'Saving...' : 'Save Cookie Settings'}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
          </>
          )}
          
          {/* Deployment Version */}
          {deploymentVersion && (
            <div className="mt-6 p-4 bg-slate-50 rounded-lg border border-slate-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-medium text-slate-700">Deployment Version</h3>
                  <p className="text-xs text-slate-500 mt-1">
                    <span className="font-mono bg-slate-200 px-2 py-0.5 rounded">{deploymentVersion.version}</span>
                    <span className="ml-2">Deployed: {deploymentVersion.date}</span>
                  </p>
                  {deploymentVersion.notes && (
                    <p className="text-xs text-slate-400 mt-1">{deploymentVersion.notes}</p>
                  )}
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    navigator.clipboard.writeText(deploymentVersion.version);
                    toast.success('Version copied to clipboard');
                  }}
                >
                  Copy Version
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Go Live Modal */}
      <GoLiveModal isOpen={showGoLiveModal} onClose={() => setShowGoLiveModal(false)} />
    </div>
  );
};

export default Settings;
